import type {
  Item, StockLevel, StockTransfer, StockTransferCreateInput, TransferImportError,
  TransferImportParseResult, Warehouse,
} from '@erp/shared';
import type Decimal from 'decimal.js';
import { ArrowLeft, ArrowLeftRight, FileDown, FileSpreadsheet, ScanLine, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Button, Card, Combobox, type ComboboxOption, FormField, Input, NumberInput, Skeleton,
} from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/cn.js';
import { trimMoney } from '../../lib/money.js';
import { normalizeNumericInput, safeDecimal } from '../../lib/numerals.js';

/**
 * One grid row. The item's display fields are SNAPSHOTTED here at add time —
 * the catalogue is 9,208 items, so there is no loaded item list to look names
 * up in later, and the transfer line on the wire carries only itemId+quantity.
 */
interface TransferLine {
  /** Stable client key so deleting a middle row can't hand its DOM to the next one. */
  _key: string;
  itemId: string;
  code: string;
  name: string;
  /** Base unit of measure — the footer totals group by this, never across it. */
  uom: string;
  /** Quantity in BASE units, raw ASCII decimal string. */
  quantity: string;
}

let lineKeySeq = 0;
const nextLineKey = (): string => `tl-${++lineKeySeq}`;

/** ~One keystroke of silence before the catalogue is asked. */
const SEARCH_DEBOUNCE_MS = 250;
/** Enough rows to disambiguate a name; small enough to answer instantly. */
const SEARCH_LIMIT = 30;

export interface StockTransferFormPageProps {
  /** When true, hydrate from `:id` and PATCH instead of POST. Drafts only. */
  edit?: boolean;
}

/**
 * Invoice-style transfer form for daily heavy use.
 *
 * The point of the page is the line grid under one scan/search box: scan a
 * barcode (or type a name) and the line appears with the source warehouse's
 * on-hand next to it. Item search is SERVER-side by construction — the live
 * catalogue is 9,208 items, so any "load the list once" approach is broken
 * before it starts.
 *
 * Nothing moves from here. Saving writes (or rewrites) a DRAFT; stock leaves
 * the source warehouse only when someone ships it on the detail page.
 */
export function StockTransferFormPage({ edit = false }: StockTransferFormPageProps): JSX.Element {
  const { t, i18n } = useTranslation('inventory');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [warehouses, setWarehouses] = useState<Warehouse[] | null>(null);
  /**
   * The branches this user actually holds — `null` means unrestricted.
   * The picker lists EVERY warehouse (a transfer's other end is by definition
   * someone else's branch), so this is what tells the two apart.
   */
  const [allowedWh, setAllowedWh] = useState<string[] | null>(null);
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<TransferLine[]>([]);
  const [hydrated, setHydrated] = useState(!edit);
  const [saving, setSaving] = useState(false);
  /** What is typed in the scan box right now (observed, see the wrapper div). */
  const [term, setTerm] = useState('');
  /** Search results together with the term that produced them — options must
   *  never claim to match a NEWER term than the one the server answered. */
  const [search, setSearch] = useState<{ q: string; items: Item[] }>({ q: '', items: [] });
  /** On-hand cache, keyed `${warehouseId}:${itemId}` — switching the source
   *  back and forth must not refetch what was already answered. */
  const [levels, setLevels] = useState<Record<string, string>>({});
  /** Excel import in flight / rows the last import could not understand. */
  const [importing, setImporting] = useState(false);
  const [importErrors, setImportErrors] = useState<TransferImportError[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  // Every Item this page has ever seen (search, scan resolve, edit hydrate),
  // so a pick or a duplicate scan can read units/uom without another request.
  const itemsById = useRef(new Map<string, Item>());
  // Read by the levels effect without depending on `levels` (that would loop).
  const levelsRef = useRef(levels);
  levelsRef.current = levels;
  /** Level fetches in flight — the effect refires on every line edit. */
  const pendingLevels = useRef(new Set<string>());
  const scanWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // scope=all: a keeper confined to one branch still has to name the other
    // end. Narrowed, this form told them "a transfer needs two warehouses".
    void api()
      .get<{ items: Warehouse[]; allowedWarehouseIds: string[] | null }>('/warehouses', { params: { scope: 'all' } })
      .then((r) => {
        setWarehouses(r.data.items);
        setAllowedWh(r.data.allowedWarehouseIds ?? null);
      })
      .catch(() => setWarehouses([]));
  }, []);

  // Pre-select the user's own branch as the SOURCE. Sending stock out is the
  // common case, and it means a keeper who holds exactly one warehouse never
  // has to think about which end is theirs. Only when nothing is chosen yet,
  // so it never fights the hydrated draft on an edit.
  useEffect(() => {
    if (edit || fromId || !allowedWh || allowedWh.length === 0) return;
    setFromId(allowedWh[0] ?? '');
  }, [edit, fromId, allowedWh]);

  // Edit: hydrate the draft. The wire line is itemId+quantity only, so each
  // item's name/code/uom is fetched by id — bounded by the draft's line count,
  // never by the catalogue.
  useEffect(() => {
    if (!edit || !id) return undefined;
    let on = true;
    void (async () => {
      try {
        const r = await api().get<StockTransfer>(`/stock-transfers/${id}`);
        if (!on) return;
        // Editing a shipped/received/cancelled transfer is not a thing — the
        // stock (or the record) has already moved on. Land on the detail page.
        if (r.data.status !== 'draft') {
          navigate(`/inventory/transfers/${id}`, { replace: true });
          return;
        }
        setFromId(r.data.fromWarehouseId);
        setToId(r.data.toWarehouseId);
        setNotes(r.data.notes ?? '');
        const metas = await Promise.all(r.data.lines.map(async (l) => {
          try { return (await api().get<Item>(`/items/${l.itemId}`)).data; } catch { return null; }
        }));
        if (!on) return;
        for (const m of metas) if (m) itemsById.current.set(m.id, m);
        setLines(r.data.lines.map((l) => {
          const m = itemsById.current.get(l.itemId);
          return {
            _key: nextLineKey(),
            itemId: l.itemId,
            code: m?.code ?? '',
            // A deleted item still has a quantity on the draft — show something
            // rather than dropping the line silently.
            name: m?.name ?? l.itemId.slice(0, 8),
            uom: m?.unitOfMeasure ?? 'ea',
            quantity: trimMoney(l.quantity),
          };
        }));
        setHydrated(true);
      } catch {
        if (!on) return;
        toast.error(t('transfers.loadFailed', { defaultValue: 'Could not load the transfer.' }));
        navigate('/inventory/transfers', { replace: true });
      }
    })();
    return () => { on = false; };
  }, [edit, id, navigate, t]);

  // Debounced SERVER search — /items?q= matches code/name/barcode plus unit,
  // variant and alias barcodes, none of which a client-side list could carry
  // for 9,208 items.
  useEffect(() => {
    const q = term.trim();
    if (!q) { setSearch({ q: '', items: [] }); return undefined; }
    let on = true;
    const handle = window.setTimeout(() => {
      void api().get<{ items: Item[] }>('/items', { params: { q, limit: SEARCH_LIMIT } })
        .then((r) => {
          if (!on) return;
          for (const it of r.data.items) itemsById.current.set(it.id, it);
          setSearch({ q, items: r.data.items.filter((it) => it.isActive) });
        })
        .catch(() => { if (on) setSearch({ q, items: [] }); });
    }, SEARCH_DEBOUNCE_MS);
    return () => { on = false; window.clearTimeout(handle); };
  }, [term]);

  // On-hand at the FROM warehouse for every line that doesn't have it yet.
  // Fetched per item (limit 1) — precise, and bounded by the grid, not the
  // catalogue. Failures (e.g. no stock.read) just leave the column blank.
  useEffect(() => {
    if (!fromId) return undefined;
    const missing = lines
      .map((l) => l.itemId)
      .filter((itemId) => {
        const key = `${fromId}:${itemId}`;
        return levelsRef.current[key] === undefined && !pendingLevels.current.has(key);
      });
    if (missing.length === 0) return undefined;
    const wh = fromId;
    for (const itemId of missing) pendingLevels.current.add(`${wh}:${itemId}`);
    void Promise.all(missing.map(async (itemId) => {
      try {
        const r = await api().get<{ items: StockLevel[] }>('/stock/levels', {
          params: { itemId, warehouseId: wh, limit: 1 },
        });
        // No row means the item was never stocked there — a real zero.
        return [itemId, r.data.items[0]?.quantity ?? '0'] as const;
      } catch {
        return [itemId, null] as const;
      }
    })).then((pairs) => {
      for (const [itemId] of pairs) pendingLevels.current.delete(`${wh}:${itemId}`);
      // Written even if the source was switched meanwhile: the key carries the
      // warehouse, so the answer stays true — and is the cache for switching back.
      setLevels((prev) => {
        const next = { ...prev };
        for (const [itemId, qty] of pairs) if (qty !== null) next[`${wh}:${itemId}`] = qty;
        return next;
      });
    });
    return undefined;
  }, [fromId, lines]);

  const warehouseOptions = useMemo<ComboboxOption[]>(
    () => (warehouses ?? [])
      // Keep an inactive warehouse visible if a hydrated draft points at it —
      // hiding it would silently blank the header on save.
      .filter((w) => w.isActive || w.id === fromId || w.id === toId)
      .map((w) => ({ value: w.id, label: w.name })),
    [warehouses, fromId, toId],
  );

  const scanOptions = useMemo<ComboboxOption[]>(
    () => search.items.map((it) => ({
      value: it.id,
      label: `${it.code} — ${it.name}`,
      sublabel: it.barcode ?? undefined,
      // The Combobox re-filters options against the typed text. The server
      // already matched these rows — collation-aware, and on unit/alias
      // barcodes the label can't reproduce — so the answered term is planted
      // in the keywords to make that re-filter a pass-through, never a veto.
      keywords: `${it.code} ${it.name} ${it.barcode ?? ''} ${search.q}`,
    })),
    [search],
  );

  if (!warehouses || !hydrated) return <Card><Skeleton className="h-40 w-full" /></Card>;

  const activeWarehouseCount = warehouses.filter((w) => w.isActive).length;
  // Same warehouse both ends is the one mistake the form can catch on its own;
  // everything else (stock on hand, a closed period) is the server's to judge.
  const sameWarehouse = !!fromId && fromId === toId;
  // A scoped user must be ONE of the two ends. Either direction is fine —
  // sending their stock out, or asking another branch to send some in — but a
  // transfer between two branches that are not theirs is the server's 403, and
  // catching it here explains why instead of failing on save.
  const foreignBoth = allowedWh !== null && !!fromId && !!toId
    && !allowedWh.includes(fromId) && !allowedWh.includes(toId);
  const usable = lines.filter((l) => safeDecimal(l.quantity).gt(0));
  const canSubmit = !!fromId && !!toId && !sameWarehouse && !foreignBoth && usable.length > 0 && !saving;

  // Total quantity PER unit of measure. One number across "12 pcs + 3 m" would
  // be arithmetic on unlike things, so each uom gets its own pill.
  const totalsByUom = ((): Array<{ uom: string; qty: string }> => {
    const byUom = new Map<string, Decimal>();
    for (const l of usable) {
      const q = safeDecimal(l.quantity);
      byUom.set(l.uom, (byUom.get(l.uom) ?? safeDecimal(0)).plus(q));
    }
    return [...byUom.entries()].map(([uom, qty]) => ({ uom, qty: qty.toString() }));
  })();

  /** The Combobox refocuses itself on a pick; this covers the async paths
   *  (barcode resolve) and Enter inside a qty box. */
  const focusScan = (): void => {
    scanWrapRef.current?.querySelector('input')?.focus();
  };

  /** Add `addBase` BASE units of an item — incrementing the existing line
   *  rather than duplicating it, so scanning the same product twice reads as
   *  qty 2, not two rows. */
  const addItem = (item: Item, addBase: string): void => {
    itemsById.current.set(item.id, item);
    setLines((prev) => {
      const existing = prev.find((l) => l.itemId === item.id);
      if (existing) {
        return prev.map((l) => (l.itemId === item.id
          ? { ...l, quantity: safeDecimal(l.quantity).plus(safeDecimal(addBase)).toString() }
          : l));
      }
      return [...prev, {
        _key: nextLineKey(),
        itemId: item.id,
        code: item.code,
        name: item.name,
        uom: item.unitOfMeasure,
        quantity: trimMoney(addBase),
      }];
    });
    focusScan();
  };

  const onScanPick = (value: string | undefined): void => {
    if (!value) return;
    // The Combobox clears its own text programmatically, which fires no input
    // event for the wrapper to observe — drop the answered search here too, or
    // the NEXT scan (which outruns the debounce) is Enter-matched against this
    // pick's leftover options and a substring hit adds the wrong product.
    setTerm('');
    setSearch({ q: '', items: [] });
    const item = itemsById.current.get(value);
    if (item) addItem(item, '1');
  };

  /**
   * A scanned code that matched no loaded option (a scanner outruns the 250ms
   * debounce by design) — resolve it server-side with the same precedence as
   * the POS scanner: item code → item barcode → unit/alias barcode.
   *
   * Transfer lines carry BASE units only, so a pack/carton barcode is
   * CONVERTED, not refused: one scan of a ×12 carton adds 12 base units.
   * Refusing would make carton-labelled stock untransferable by scan.
   */
  const resolveScan = async (code: string): Promise<void> => {
    // Drop the pending q-search for the barcode text — it already resolved.
    setTerm('');
    setSearch({ q: '', items: [] });
    try {
      const { data } = await api().get<{ item: Item; itemUnitId: string | null }>(
        '/items/resolve', { params: { barcode: code } },
      );
      const unit = data.itemUnitId ? data.item.units.find((u) => u.id === data.itemUnitId) : undefined;
      if (unit) {
        // Say the conversion out loud (stable id — repeated carton scans
        // update one toast instead of stacking a wall of them).
        toast.info(
          t('transfers.packScanned', {
            unit: unit.name,
            factor: trimMoney(unit.factor),
            uom: data.item.unitOfMeasure,
            defaultValue: 'Pack barcode: {{unit}} — added {{factor}} {{uom}}.',
          }),
          { id: 'transfer-pack-scan' },
        );
      }
      addItem(data.item, unit ? unit.factor : '1');
    } catch {
      toast.error(t('transfers.unknownBarcode', { defaultValue: 'No product found for that barcode.' }));
    }
  };

  /** Template language follows the UI: Arabic UI → Arabic headers. */
  const templateLang = i18n.language.startsWith('en') ? 'en' : 'ar';

  const downloadTemplate = async (): Promise<void> => {
    try {
      const res = await api().get('/stock-transfers/import/template', {
        params: { lang: templateLang }, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transfer-template-${templateLang}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('transfers.templateFailed', { defaultValue: "Couldn't download the template." }));
    }
  };

  /**
   * A filled template → lines in the grid. The server resolves each barcode
   * exactly like the scan box (carton barcode ⇒ ×factor base units); rows it
   * could not understand come back per-row and are shown under the grid —
   * the readable rows are never held hostage by the broken ones.
   */
  const importFile = async (file: File): Promise<void> => {
    setImporting(true);
    try {
      const body = await file.arrayBuffer();
      const { data } = await api().post<TransferImportParseResult>(
        '/stock-transfers/import/parse', body,
        { params: { locale: i18n.language }, headers: { 'Content-Type': 'application/octet-stream' } },
      );
      if (data.lines.length > 0) {
        setLines((prev) => {
          const next = [...prev];
          for (const l of data.lines) {
            const existing = next.find((x) => x.itemId === l.itemId);
            if (existing) {
              existing.quantity = safeDecimal(existing.quantity).plus(safeDecimal(l.baseQuantity)).toString();
            } else {
              next.push({
                _key: nextLineKey(), itemId: l.itemId, code: l.code, name: l.name,
                uom: l.uom, quantity: trimMoney(l.baseQuantity),
              });
            }
          }
          return next;
        });
      }
      setImportErrors(data.errors);
      if (data.lines.length > 0) {
        toast.success(t('transfers.importAdded', {
          count: data.lines.length,
          defaultValue: 'Imported {{count}} rows into the transfer.',
        }));
      } else {
        toast.error(t('transfers.importNothing', { defaultValue: 'No usable rows in that file.' }));
      }
    } catch {
      toast.error(t('transfers.importFailed', { defaultValue: "Couldn't read that file. Use the template: barcode, quantity." }));
    } finally {
      setImporting(false);
      // Same file re-picked must fire onChange again.
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const updateQty = (key: string, raw: string): void => {
    // Arabic/Persian digits → ASCII at the chokepoint, same as every document
    // form — what is stored is always what Decimal (and the API) can parse.
    const clean = normalizeNumericInput(raw);
    setLines((prev) => prev.map((l) => (l._key === key ? { ...l, quantity: clean } : l)));
  };

  const removeLine = (key: string): void => {
    setLines((prev) => prev.filter((l) => l._key !== key));
  };

  const submit = async (): Promise<void> => {
    if (!canSubmit || !fromId || !toId) return;
    setSaving(true);
    try {
      const body: StockTransferCreateInput = {
        fromWarehouseId: fromId,
        toWarehouseId: toId,
        notes: notes.trim() || null,
        // Wire contract is ^\d+(\.\d{1,4})?$ — re-serialize through Decimal so
        // a half-typed "3." can never 400 the whole draft.
        lines: usable.map((l) => ({
          itemId: l.itemId,
          quantity: safeDecimal(l.quantity).toDecimalPlaces(4).toString(),
        })),
      };
      if (edit && id) {
        // PATCH takes the same shape as create: full header + lines, replaced
        // wholesale server-side.
        await api().patch(`/stock-transfers/${id}`, body);
        navigate(`/inventory/transfers/${id}`);
      } else {
        const r = await api().post<StockTransfer>('/stock-transfers', body);
        navigate(`/inventory/transfers/${r.data.id}`);
      }
    } catch {
      toast.error(edit
        ? t('transfers.updateFailed', { defaultValue: 'Could not save the transfer.' })
        : t('transfers.createFailed', { defaultValue: 'Could not create the transfer.' }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Link to="/inventory/transfers" className="inline-flex w-fit items-center gap-1 text-sm text-fg-muted hover:text-fg">
        <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" aria-hidden />
        {t('transfers.title', { defaultValue: 'Stock transfers' })}
      </Link>

      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <ArrowLeftRight className="h-6 w-6 text-brand" aria-hidden />
          {edit
            ? t('transfers.editTitle', { defaultValue: 'Edit transfer' })
            : t('transfers.new', { defaultValue: 'New transfer' })}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">
          {t('transfers.newHint', { defaultValue: 'This creates a draft. Nothing leaves the source warehouse until you ship it.' })}
        </p>
      </header>

      {activeWarehouseCount < 2 && (
        <Card className="p-4 text-sm text-fg-muted">
          {t('transfers.needTwo', { defaultValue: 'A transfer needs two warehouses. Add another one first.' })}
        </Card>
      )}

      <Card>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField id="tf-from" label={t('transfers.from', { defaultValue: 'From warehouse' })} required>
            <Combobox
              id="tf-from"
              options={warehouseOptions}
              value={fromId || undefined}
              onChange={(v) => setFromId(v ?? '')}
              ariaLabel={t('transfers.from', { defaultValue: 'From warehouse' })}
            />
          </FormField>
          <FormField
            id="tf-to"
            label={t('transfers.to', { defaultValue: 'To warehouse' })}
            required
            error={sameWarehouse
              ? t('transfers.sameWarehouse', { defaultValue: 'Pick a different warehouse.' })
              : foreignBoth
                ? t('transfers.needOwnBranch', { defaultValue: 'One end must be your own warehouse.' })
                : undefined}
          >
            <Combobox
              id="tf-to"
              options={warehouseOptions}
              value={toId || undefined}
              onChange={(v) => setToId(v ?? '')}
              ariaLabel={t('transfers.to', { defaultValue: 'To warehouse' })}
            />
          </FormField>
          <FormField id="tf-notes" label={t('transfers.notes', { defaultValue: 'Notes (optional)' })}>
            <Input id="tf-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </FormField>
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-2">
        {/* The Combobox keeps its typed text to itself; the native `input` event
            bubbling out of its inner <input> is observed here instead of forking
            the shared component just to add an onQuery prop. The Excel controls
            live OUTSIDE this wrapper on purpose: a file pick fires an `input`
            event too, and observed here it would become a search for
            "C:\fakepath\…". */}
        <div
          ref={scanWrapRef}
          onInput={(e) => setTerm((e.target as HTMLInputElement).value)}
          className="flex min-w-64 flex-1 items-center gap-2 rounded-lg border border-border bg-bg-subtle px-3 py-2"
        >
          <ScanLine className="h-5 w-5 flex-none text-fg-muted" aria-hidden />
          <div className="flex-1">
            <Combobox
              options={scanOptions}
              onChange={onScanPick}
              onEnterUnmatched={(code) => void resolveScan(code)}
              clearOnSelect
              autoFocus
              placeholder={t('transfers.scanAdd', { defaultValue: 'Scan a barcode or search to add a product…' })}
              ariaLabel={t('transfers.scanAdd', { defaultValue: 'Scan a barcode or search to add a product…' })}
            />
          </div>
        </div>
        {/* Excel path for the counted-on-paper move: template out, filled sheet in. */}
        <Button type="button" variant="secondary" onClick={() => void downloadTemplate()}>
          <FileDown className="h-4 w-4" aria-hidden />
          {t('transfers.template', { defaultValue: 'Template' })}
        </Button>
        <Button type="button" variant="secondary" loading={importing} onClick={() => fileRef.current?.click()}>
          <FileSpreadsheet className="h-4 w-4" aria-hidden />
          {t('transfers.importExcel', { defaultValue: 'Import Excel' })}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void importFile(f);
          }}
        />
      </div>

      {importErrors.length > 0 && (
        <Card className="border-warning/40 bg-warning/5 p-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-sm font-semibold">
              {t('transfers.importErrors', {
                count: importErrors.length,
                defaultValue: '{{count}} rows were not imported',
              })}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={t('transfers.dismissErrors', { defaultValue: 'Dismiss' })}
              onClick={() => setImportErrors([])}
            >
              <X className="h-4 w-4" aria-hidden />
            </Button>
          </div>
          <ul className="space-y-1 text-sm text-fg-muted">
            {importErrors.slice(0, 30).map((er) => (
              <li key={`${er.rowNumber}-${er.barcode}`} className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-bg-muted px-1.5 py-0.5 font-mono text-xs">
                  {t('transfers.importRow', { n: er.rowNumber, defaultValue: 'Row {{n}}' })}
                </span>
                {er.barcode && <span className="font-mono text-xs">{er.barcode}</span>}
                <span>
                  {er.reason === 'unknown_barcode'
                    ? t('transfers.reasonUnknownBarcode', { defaultValue: 'No product with this barcode.' })
                    : t('transfers.reasonBadQuantity', { defaultValue: 'Quantity is missing or not a number.' })}
                </span>
              </li>
            ))}
            {importErrors.length > 30 && (
              <li>{t('transfers.moreErrors', { count: importErrors.length - 30, defaultValue: '…and {{count}} more' })}</li>
            )}
          </ul>
        </Card>
      )}

      <Card className="overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-bg-muted text-fg-subtle">
            <tr>
              <th className="w-10 px-3 py-2 text-start font-medium">#</th>
              <th className="px-3 py-2 text-start font-medium">{t('transfers.item', { defaultValue: 'Product' })}</th>
              {/* Base unit of measure — sits before the numbers so both "on
                  hand" and "qty" read as counts OF this unit. */}
              <th className="w-24 px-3 py-2 text-start font-medium">{t('transfers.unit', { defaultValue: 'Unit' })}</th>
              <th className="w-28 px-3 py-2 text-end font-medium">{t('transfers.onHand', { defaultValue: 'On hand' })}</th>
              <th className="w-32 px-3 py-2 text-end font-medium">{t('transfers.qty', { defaultValue: 'Qty' })}</th>
              <th className="w-12" />
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-sm text-fg-muted">
                  {t('transfers.emptyGrid', { defaultValue: 'Scan a barcode or search above to add the first product.' })}
                </td>
              </tr>
            ) : lines.map((l, idx) => {
              const level = fromId ? levels[`${fromId}:${l.itemId}`] : undefined;
              // Warn, never block: the server enforces stock at SHIP time, and
              // a draft is often written before the goods are counted —
              // blocking here would forbid planning tomorrow's move with
              // today's numbers.
              const over = level !== undefined && safeDecimal(l.quantity).gt(safeDecimal(level));
              return (
                <tr key={l._key} className="border-t border-border">
                  <td className="px-3 py-2 text-fg-muted">{idx + 1}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium">{l.name}</div>
                    <div className="font-mono text-xs text-fg-muted">{l.code}</div>
                  </td>
                  <td className="px-3 py-2 text-fg-muted">{l.uom}</td>
                  <td className="px-3 py-2 text-end font-mono text-fg-muted">
                    {level !== undefined ? trimMoney(level) : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <NumberInput
                      className={cn('text-end font-mono', over && 'text-warning')}
                      value={l.quantity}
                      onValueChange={(raw) => updateQty(l._key, raw)}
                      onKeyDown={(e) => {
                        // Scan flow: type/adjust a qty, Enter, scan the next item.
                        if (e.key === 'Enter') { e.preventDefault(); focusScan(); }
                      }}
                      title={over ? t('transfers.overOnHand', {
                        defaultValue: 'More than is on hand at the source warehouse. The draft can still be saved — stock is enforced when it ships.',
                      }) : undefined}
                      aria-label={t('transfers.qty', { defaultValue: 'Qty' })}
                    />
                  </td>
                  <td className="px-3 py-2 text-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={t('transfers.removeLine', { defaultValue: 'Remove line' })}
                      onClick={() => removeLine(l._key)}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Card className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
          <span className="text-fg-muted">
            {t('transfers.lines', { defaultValue: 'Items' })}:{' '}
            <span className="font-semibold text-fg">{usable.length}</span>
          </span>
          {totalsByUom.length > 0 && (
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="text-fg-muted">{t('transfers.totalQty', { defaultValue: 'Total quantity' })}:</span>
              {totalsByUom.map((row) => (
                <span key={row.uom} className="rounded-full border border-border bg-bg-subtle px-2.5 py-0.5 font-mono text-xs">
                  {row.qty} {row.uom}
                </span>
              ))}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {lines.length === 0 && (
            <span className="text-xs text-fg-muted">
              {t('transfers.emptyGrid', { defaultValue: 'Scan a barcode or search above to add the first product.' })}
            </span>
          )}
          <Button
            type="button"
            variant="secondary"
            disabled={saving}
            onClick={() => navigate(edit && id ? `/inventory/transfers/${id}` : '/inventory/transfers')}
          >
            {t('transfers.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="button" disabled={!canSubmit} loading={saving} onClick={() => void submit()}>
            {edit
              ? t('transfers.save', { defaultValue: 'Save changes' })
              : t('transfers.create', { defaultValue: 'Create draft' })}
          </Button>
        </div>
      </Card>
    </div>
  );
}
